import React, { useEffect, useState, useRef, useContext } from "react";
import styles from "./ManageCampaigns.module.css";
import { getPageFromName } from "../../route_utils";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../context/AuthContext/AuthContext";
import api_endpoints from "../../api";
import { numberWithCommas } from "../../utils";
import { v4 as uuidv4 } from "uuid";
import {
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Typography,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ImageIcon from "@mui/icons-material/Image";
import DataGrid from "../../widgets/DataGrid/DataGrid";
import CustomTextField from "../../widgets/CustomTextField/CustomTextField";
import CustomSelect from "../../widgets/CustomSelect/CustomSelect";
import axios from "axios";
import { NavigationButton } from "../../widgets/NavigationButton/NavigationButton";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import LoadingContainer from "../LoadingContainer/LoadingContainer";

const ManageCampaigns = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [loadingError, setLoadingError] = useState(false);
  const [fetchingTableData, setFetchingTableData] = useState(false);
  const [completionData, setCompletionData] = useState([]);
  const [modalStatusOpen, setmodalStatusOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const cancelTokenSource = useRef(null);
  const { t } = useCustomTranslation("Campagnes");

  const [viewMode, setViewMode] = useState("card"); // 'card' | 'table'

  const getTranslatedLabel = (key, fallback) => {
    const translated = t(key);
    return translated && translated !== key ? translated : fallback;
  };

  const getKioskVisibilityLabel = (isHidden) =>
    isHidden
      ? getTranslatedLabel("hidden_in_kiosk", "Hidden in Kiosk")
      : getTranslatedLabel("visible_in_kiosk", "Visible in Kiosk");

  const [tableFilter, setTableFilter] = useState({
    campaignName: "",
    campaignCreator: "",
    donationAmount: "",
    status: "",
    acceptCash: "",
    is_archived: "",
  });

  const columns = [
    {
      field: "campaignName",
      headerName: t("campaign_name"),
      sortable: true,
      numberSort: false,
    },
    {
      field: "campaignCreator",
      headerName: t("created_by"),
      sortable: true,
      numberSort: false,
    },
    {
      field: "donationAmount",
      headerName: t("donation_amount"),
      sortable: true,
      numberSort: true,
      renderCell: (params) => {
        const raw = (params.value ?? 0).toString().replace(/,/g, "");
        return `$${Number(raw).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    },
    {
      field: "status",
      headerName: t("status"),
      sortable: true,
      numberSort: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t("active_status") : t("inActive_status")}
          color={params.value ? "success" : "error"}
          variant="contained"
        />
      ),
    },
    {
      field: "hideInKiosk",
      headerName: getTranslatedLabel("kiosk", "Kiosk"),
      sortable: true,
      numberSort: false,
      renderCell: (params) => (
        <Chip
          label={getKioskVisibilityLabel(params.value)}
          color={params.value ? "error" : "success"}
          variant="contained"
        />
      ),
    },
    {
      field: "acceptCash",
      headerName: t("accept_cash"),
      sortable: true,
      numberSort: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t("yes_text") : t("no_text")}
          color={params.value ? "success" : "error"}
          variant="contained"
        />
      ),
    },
    {
      field: "is_archived",
      headerName: t("archive"),
      sortable: true,
      numberSort: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t("yes_text") : t("no_text")}
          color={params.value ? "success" : "error"}
          variant="contained"
        />
      ),
    },
  ];

  const filters = {
    campaignName: (
      <CustomTextField
        value={tableFilter.campaignName}
        onChange={(e) => changeTableFilter("campaignName", e.target.value)}
        clearFilter={() => clearTableFilter("campaignName")}
      />
    ),
    campaignCreator: (
      <CustomTextField
        value={tableFilter.campaignCreator || ""}
        onChange={(e) => changeTableFilter("campaignCreator", e.target.value)}
        clearFilter={() => clearTableFilter("campaignCreator")}
      />
    ),
    donationAmount: (
      <CustomTextField
        value={tableFilter.donationAmount || ""}
        onChange={(e) => changeTableFilter("donationAmount", e.target.value)}
        clearFilter={() => clearTableFilter("donationAmount")}
      />
    ),
    status: (
      <CustomSelect
        value={tableFilter.status}
        onChange={(e) => {
          changeTableFilter("status", e.target.value);
        }}
        options={[
          { value: "", label: t("all_text") },
          { value: true, label: t("active_status") },
          { value: false, label: t("inActive_status") },
        ]}
      />
    ),
    acceptCash: (
      <CustomSelect
        value={tableFilter.acceptCash}
        onChange={(e) => {
          changeTableFilter("acceptCash", e.target.value);
        }}
        options={[
          { value: "", label: t("all_text") },
          { value: true, label: t("yes_text") },
          { value: false, label: t("no_text") },
        ]}
      />
    ),
    is_archived: (
      <CustomSelect
        value={tableFilter.is_archived}
        onChange={(e) => {
          changeTableFilter("is_archived", e.target.value);
        }}
        options={[
          { value: "", label: t("all_text") },
          { value: true, label: t("yes_text") },
          { value: false, label: t("no_text") },
        ]}
      />
    ),
  };

  useEffect(() => {
    fetchPageData();
  }, []);

  useEffect(() => {
    fetchPageData();
  }, [authContext.authContext.accessToken.organization_id]);

  useEffect(() => {
    getData();
    return () => {
      if (cancelTokenSource.current) {
        cancelTokenSource.current.cancel(
          "Operation canceled due to component unmount."
        );
      }
    };
  }, [tableFilter, authContext.authContext.accessToken.organization_id]);

  const fetchPageData = async () => {
    setLoading(true);
    await fetchCampaignsStatus();
    await fetchCampaigns();
    setLoading(false);
  };

  const fetchCampaigns = async () => {
    setFetchingTableData(true);
    try {
      let response = await api_endpoints.getCampaigns(
        authContext.authContext.accessToken.organization_id,
        tableFilter.campaignName,
        tableFilter.campaignCreator,
        tableFilter.donationAmount,
        tableFilter.status,
        tableFilter.acceptCash,
        tableFilter.is_archived
      );

      if (response.status === 200) {
        // include fields needed for both views (table + card)
        return response.data.map((campaign) => ({
          id: campaign._id,
          campaignId: campaign._id,
          image_url: campaign.image_url,
          acceptCash: campaign.can_donate_with_cash,
          hideInKiosk: Boolean(campaign.hide_in_kiosk),
          campaignName: campaign.name,
          campaignCreator: `${campaign.created_by.last_name} ${campaign.created_by.first_name}`,
          donationAmount: numberWithCommas(
            (campaign?.amountDonated ?? 0).toFixed(2)
          ),
          status: campaign.enabled,
          is_archived: campaign.is_archived,
        }));
      }

      setFetchingTableData(false);
    } catch (e) {
      setFetchingTableData(false);
      setLoading(false);
    }
  };
 
  const fetchCampaignsStatus = async () => {
    try {
      let response = await api_endpoints.getCampaignsStatus(
        authContext.authContext.accessToken.organization_id
      );

    if (response.status === 200) {
        setCompletionData(response.data);
      }
    } catch (e) {
      setLoading(false);
    }
  };

  const selectCampaign = (eOrRow) => {
    // supports both DataGrid row (with .id) and card click (passing the row object)
    const id = eOrRow?.id ?? eOrRow;
    navigate(getPageFromName("Campagne").path, {
      state: { campaign_id: id },
    });
  };

  const changeTableFilter = (option, value) => {
    setTableFilter({ ...tableFilter, [option]: value });
  };

  const clearTableFilter = (option) => {
    setTableFilter((prevFilter) => ({ ...prevFilter, [option]: "" }));
  };

  const getData = async () => {
    setLoading(true);
    setLoadingError(false);
    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel("Operation canceled due to new request.");
    }
    cancelTokenSource.current = axios.CancelToken.source();
    try {
      const data = await fetchCampaigns(cancelTokenSource.current.token);
      setRows(data || []);
    } catch (error) {
      if (axios.isCancel(error)) {
        // noop
      } else {
        console.error(error);
        setLoadingError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={"flex justify-end"}>
        <NavigationButton
          startIcon={<AddIcon />}
          text={"Campagne"}
          path={getPageFromName("Créer une campagne").path}
        />
      </div>

      <div className={styles.header}>
        <div className={styles.left_header}></div>
      </div>

      {/* Progress / Status section (unchanged) */}
      <div className={styles.stats_container}>
        <div className={styles.charts_wrapper}>
          <div className="container-white flex-col mt-4">
            <div>
              <div className="mb-4 flex w-full text-lg">
                {t("progress_graph_title")}
              </div>
              {completionData.length > 0 ? (
                <React.Fragment key={uuidv4()}>
                  <div className="overflow-hidden">
                    {completionData.slice(0, 3).map((data) => (
                      <div className={styles.bar_chart_data_wrapper} key={uuidv4()}>
                        <div className={styles.bar_chart_title}>
                          <label style={{ justifyContent: "flex-start" }}>
                            {data.name}
                          </label>
                          <label style={{ fontSize: 14 }}>
                            {((data.amountDonated / data.objective) * 100).toFixed(2)} %
                          </label>
                          <label style={{ justifyContent: "flex-end" }}>
                            {numberWithCommas(data.objective)}
                          </label>
                        </div>
                        <div className={styles.bar_chart_progress}>
                          <div
                            style={{
                              width:
                                data.amountDonated / data.objective > 1
                                  ? "100%"
                                  : `${(data.amountDonated / data.objective) * 100}%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {completionData.length > 3 ? (
                    <>
                      <Dialog
                        fullWidth
                        open={modalStatusOpen}
                        onClose={() => setmodalStatusOpen(false)}
                        maxWidth="md"
                      >
                        <DialogTitle>{t("donor_list_dialogue_title")}</DialogTitle>
                        <DialogContent>
                          {completionData.map((data) => (
                            <div className={styles.bar_chart_data_wrapper} key={uuidv4()}>
                              <div className={styles.bar_chart_title}>
                                <label style={{ justifyContent: "flex-start" }}>
                                  {data.name}
                                </label>
                                <label style={{ fontSize: 14 }}>
                                  {((data.amountDonated / data.objective) * 100).toFixed(2)}{" "}
                                </label>
                                <label style={{ justifyContent: "flex-end" }}>
                                  {numberWithCommas(data.objective)}
                                </label>
                              </div>
                              <div className={styles.bar_chart_progress}>
                                <div
                                  style={{
                                    width:
                                      data.amountDonated / data.objective > 1
                                        ? "100%"
                                        : (data.amountDonated / data.objective) * 100,
                                  }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </DialogContent>
                      </Dialog>
                      <Button fullWidth onClick={() => setmodalStatusOpen(true)}>
                        {t("show_more")}
                      </Button>
                    </>
                  ) : null}
                </React.Fragment>
              ) : (
                <div>{t("progress_graph_no_data")}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* View Mode Toggle + Filters */}
      <div className="container-main !flex-row [&>div]:flex items-end space-x-4 w-full mb-3">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_, v) => v && setViewMode(v)}
        >
          <ToggleButton value="card">{t("Card View")}</ToggleButton>
          <ToggleButton value="table">{t("Table View")}</ToggleButton>
        </ToggleButtonGroup>

        {/* Keep your existing filters visible for both views */}
        <div className="flex-1 flex-col">
          {t("campaign_name")}
          {filters.campaignName}
        </div>
        <div className="flex-1 flex-col">
          {t("created_by")}
          {filters.campaignCreator}
        </div>
        <div className="flex-1 flex-col">
          {t("donation_amount")}
          {filters.donationAmount}
        </div>
        <div className="flex-1 flex-col">
          {t("status")}
          {filters.status}
        </div>
        <div className="flex-1 flex-col">
          {t("accept_cash")}
          {filters.acceptCash}
        </div>
        <div className="flex-1 flex-col">
          {t("archive")}
          {filters.is_archived}
        </div>
      </div>

      {/* Main list area: Card OR Table */}
      <div className={styles.table_container_body}>
        {viewMode === "table" ? (
          <DataGrid
            columns={columns}
            rows={rows}
            showDetails={(row) => selectCampaign(row)}
            filters={{}} // filters are shown above now
            loading={loading}
          />
        ) : (
          <LoadingContainer loading={loading} error={loadingError}>
            <Grid container spacing={2}>
              {rows.length === 0 ? (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {t("progress_graph_no_data")}
                  </Typography>
                </Grid>
              ) : (
                rows.map((campaign, index) => (
                  <Grid
                    item
                    key={campaign?.id || `campaign_${index}`}
                    xs={12}
                    sm={12}
                    md={6}
                    lg={6}
                    xl={4}
                  >
                    <Card
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        minHeight: 130,
                        height: "100%",
                        p: 1,
                        boxShadow: 1,
                        cursor: "pointer",
                        overflow: "hidden",
                      }}
                      onClick={() => selectCampaign(campaign)}
                    >
                      {campaign?.image_url ? (
                        <CardMedia
                          component="img"
                          sx={{
                            width: 120,
                            minHeight: 120,
                            alignSelf: "stretch",
                            flexShrink: 0,
                            objectFit: "cover",
                          }}
                          image={`${process.env.REACT_APP_S3_URL}${campaign?.image_url}`}
                          alt={`Campaign image for ${campaign?.campaignName || "campaign"}`}
                        />
                      ) : (
                        <Avatar sx={{ width: 120, height: 120, fontSize: "2rem" }}>
                          <ImageIcon />
                        </Avatar>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                          minWidth: 0,
                          pl: 2,
                          gap: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 1,
                            flexWrap: "wrap",
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            component="div"
                            variant="h6"
                            sx={{
                              flex: "1 1 180px",
                              minWidth: 0,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {campaign?.campaignName}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              flex: "0 1 auto",
                              minWidth: 0,
                              maxWidth: "100%",
                              flexWrap: "wrap",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Chip
                              color={campaign.status ? "success" : "error"}
                              label={campaign.status ? t("active_status") : t("inActive_status")}
                              size="small"
                            />
                            <Chip
                              color={campaign.hideInKiosk ? "error" : "success"}
                              label={getKioskVisibilityLabel(campaign.hideInKiosk)}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </Box>

                        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ overflowWrap: "anywhere" }}
                          >
                            {t("created_by")}: {campaign?.campaignCreator || "—"}
                          </Typography>
                        </CardContent>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            mt: "auto",
                            width: "100%",
                            gap: 0.75,
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{
                              maxWidth: "100%",
                              fontWeight: Number(campaign?.donationAmount ?? 0) === 0 ? 700 : 600,
                              lineHeight: 1.1,
                              fontVariantNumeric: "tabular-nums",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {`$${Number(
                            (campaign?.donationAmount ?? campaign?.amountDonated ?? 0)
                              .toString()
                              .replace(/[^\d.-]/g, "") || 0
                          ).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.75,
                              width: "100%",
                              minWidth: 0,
                              flexWrap: "wrap",
                              justifyContent: "flex-start",
                            }}
                          >
                            <Chip
                              variant="outlined"
                              size="small"
                              label={`${t("accept_cash")}: ${campaign.acceptCash ? t("yes_text") : t("no_text")}`}
                            />
                            {campaign.is_archived ? (
                              <Chip variant="outlined" size="small" color="warning" label={t("archive")} />
                            ) : null}
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          </LoadingContainer>
        )}
      </div>
    </>
  );
};

export default ManageCampaigns;
